import React, { FC, ReactNode } from 'react';
import './DataRowCard.css';
import { Header } from '@/Shared/Types/datatable.types';
import { RecordHelper } from '@/Shared/Helpers/record.helper';
import DeleteButton from '../../Buttons/DeleteButton/DeleteButton';
import EditButton from '../../Buttons/EditButton/EditButton';
import ViewButton from '../../Buttons/ViewButton/ViewButton';
import { useAnimationOnScroll } from '@/Shared/Logic/Hooks/TechnicalHooks/useAnimationOnScroll';
import PhoneLink from '../../PhoneLink/PhoneLink';
import { WidgetFormat } from '@/Shared/Types';
import { RestoreButton } from '../../Buttons';

interface DataRowCardProps<VM extends Record<string, any>> {
	newItems? : number[],
	headers: Record<keyof VM, Header<VM>>,
	row: VM,
	Icon ?: React.ElementType
	onView?: (rowId: number) => void,
	onEdit?: (rowId: number) => void,
	onDelete?: (rowId: number) => void,
	onRestore?: (rowId: number) => void,
	format?: WidgetFormat
}

const DataRowCard: FC<DataRowCardProps<any>> = ({
	newItems,
	headers,
	row,
	Icon,
	onView,
	onEdit,
	onDelete,
	onRestore,
	format = 'Card'
}) => {

	const getValue = (key: keyof any) => (row[key] === undefined || row[key] === null) ? "" : String(row[key]);

	const [ref, visible] = useAnimationOnScroll<HTMLDivElement>({}, true);
	const cardClass = getValue('cardClass');
	const headerTitle = getValue('cardHeaderTitle');
	const headerPhoneLink = getValue('cardHeaderPhoneLink');

	return (
		<div
			onClick={() => onView?.(row.id)}
			className={`DataRowCard`}
			data-testid="DataRowCard" ref={ref}
			style={{
				opacity: visible ? 1 : 0,
				transform: visible ? 'translateY(0)' : 'translateY(10px)',
				transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
				transitionDelay: `50ms`
			}}
		>
			<div className={`DataRowCard__header ${cardClass}`}>
				<span className={`DataRowCard__header-main-icon layout_domain-icon ${(newItems ?? []).includes(row.id) ? 'is-new' : ''}`}>{Icon && <Icon/>}</span>
				<div className="fixed-header-actions flex-row">
					{onView && <ViewButton onClick={() => onView(row.id)} />}
					{onEdit  && !row.isDeleted && <EditButton onClick={() => onEdit(row.id)} />}
					{onDelete && !row.isDeleted && <DeleteButton onClick={() => onDelete(row.id)} />}
					{onRestore && row.isDeleted &&  <RestoreButton onClick={() => onRestore(row.id)} />}
				</div>
				<div className='Rowcard-header-wrapper flex-row'>
					{headerTitle && <div className='Rowcard-header__title ellipsis' title={headerTitle}>{headerTitle}</div>}
					{headerPhoneLink && <PhoneLink phone={headerPhoneLink} />}
				</div>
			</div>
			{format === 'Card' &&
				<div className="DataRowCard__body">
					{
						RecordHelper.toValues(headers).map(header =>
							<div className="DataRowCard__row" key={header.key as string}>
								<span className="DataRowCard__item-label">{header.label} : </span>
								<span className="DataRowCard__item-data">{getValue(header.key)}</span>
							</div>)
					}
				</div>}
		</div>
	);
}

export default DataRowCard;
